// PiecePreview 用 Blockly 初期化（旧ビルダープレビュー blockly/initial.ts から復元・簡略化）
// 変数/関数ブロックは扱わず、enabledCommands のブロックのみを CUSTOM_FLYOUT で表示する。

/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import * as Blockly from "blockly";
import BlocklyJs from "blockly/javascript";
import { DisableTopBlocks } from "@blockly/disable-top-blocks";
import { InitMsg } from "common/blockly";
import { BuilderCommandId } from "features/builder/types";
import { Toolbox, buildFlyoutContents } from "./Toolbox";
import { PreviewBlocklyOptions } from "./BlocklyOptions";
import { InitCharacterBlock, InitControlsBlock, InitColourBlock } from "./blocks";

const updateFlyout = (workspace: Blockly.WorkspaceSvg, offset: number, scale: number) => {
  const blocklyFlyout = workspace.getFlyout() as Blockly.Flyout;
  const blockToolbox = workspace.getToolbox() as Blockly.Toolbox;
  const item = blockToolbox.getToolboxItemById("category") as Blockly.ToolboxCategory;
  if (item) {
    // setSelectedItem は同一カテゴリの再選択でトグル解除（フライアウトが閉じる）ため、
    // 閉じてしまった場合はもう一度選択して開き直す（algorithm 版 initial.ts と同じ防御）
    blockToolbox.setSelectedItem(item);
    if (!blocklyFlyout.isVisible()) {
      blockToolbox.setSelectedItem(item);
    }
  }
  // @ts-expect-error Blockly 7.x の型に positionAt_ が無いため
  blocklyFlyout.positionAt_(
    blocklyFlyout.getWidth(),
    blocklyFlyout.getHeight() - offset - 75 * scale,
    0,
    offset,
  );
};

const createStartBlock = (workspace: Blockly.WorkspaceSvg, scale: number, offset: number) => {
  const flyout = workspace.getFlyout() as Blockly.Flyout | null;
  const eventBlock = new Blockly.BlockSvg(workspace, "character_event");
  eventBlock.initSvg();
  eventBlock.render();
  eventBlock.moveBy((flyout?.getWidth() ?? 0) / scale + 50, offset);
};

const useInitPreviewWorkspace = (
  scale: number,
  offset: number,
  locale: string,
  enabledCommands: BuilderCommandId[],
  maxInstances: { [type: string]: number },
  setWorkspace: (workspace: Blockly.WorkspaceSvg) => void,
) => {
  useEffect(() => {
    InitMsg(locale);
  }, [locale]);

  useEffect(() => {
    InitCharacterBlock();
    InitControlsBlock();
    InitColourBlock();

    Blockly.WorkspaceSvg.prototype.refreshToolboxSelection = function () {
      // disable refresh toolbox（フライアウトが勝手に閉じるのを防ぐ）
    };

    const workspace: Blockly.WorkspaceSvg = Blockly.inject(
      "blocklyDiv",
      PreviewBlocklyOptions(scale, Toolbox, maxInstances),
    );
    BlocklyJs.init(workspace);

    workspace.registerToolboxCategoryCallback("CUSTOM_FLYOUT", () =>
      buildFlyoutContents(enabledCommands),
    );

    const toolbox = workspace.getToolbox() as Blockly.Toolbox;
    const div = toolbox.HtmlDiv as HTMLDivElement;
    if (div) {
      div.style.width = "0px";
      div.style.top = `${offset * scale}px`;
      div.style.display = "none";
    }

    workspace.updateToolbox(Toolbox);
    updateFlyout(workspace, offset * scale, scale);

    const blocklyFlyout = workspace.getFlyout() as Blockly.Flyout;
    blocklyFlyout.autoClose = false;

    const scrollbars = document.getElementsByClassName("blocklyScrollbarVertical");
    for (let i = 0; i < scrollbars.length; i++) {
      const e = scrollbars[i] as SVGElement;
      if (e.getAttribute("display") !== "block") {
        e.setAttribute("style", "pointer-events: none;");
      }
    }

    createStartBlock(workspace, scale, offset);

    workspace.clearUndo();
    workspace.showContextMenu = () => {};
    workspace.addChangeListener(Blockly.Events.disableOrphans);
    new DisableTopBlocks().init();
    BlocklyJs.addReservedWords("highlightBlock");

    setWorkspace(workspace);

    return () => {
      setWorkspace(null);
    };
  }, []);
};

const useWorkspaceListener = (
  scale: number,
  offset: number,
  workspace: Blockly.WorkspaceSvg,
  maxInstances: { [type: string]: number },
  setEvent: (code: string) => void,
  onRemainingChange: (remaining: { [type: string]: number }) => void,
) => {
  useEffect(() => {
    if (!workspace) return;

    workspace.setScale(scale);
    updateFlyout(workspace, offset * scale, scale);

    // のこり個数 = 上限 − workspace 上の配置数（algorithm コースと同じ考え方）
    const notifyRemaining = () => {
      const counts: { [type: string]: number } = {};
      workspace.getAllBlocks(false).forEach((block) => {
        counts[block.type] = (counts[block.type] ?? 0) + 1;
      });
      const remaining: { [type: string]: number } = {};
      Object.keys(maxInstances).forEach((type) => {
        remaining[type] = Math.max(0, maxInstances[type] - (counts[type] ?? 0));
      });
      onRemainingChange(remaining);
    };
    notifyRemaining();

    const onWorkspaceChange = (event: { type: string; oldJson?: { type?: string } }) => {
      if (
        event.type !== Blockly.Events.BLOCK_CHANGE &&
        event.type !== Blockly.Events.BLOCK_CREATE &&
        event.type !== Blockly.Events.BLOCK_DELETE &&
        event.type !== Blockly.Events.BLOCK_MOVE
      ) {
        return;
      }

      // 開始ブロックが消えたら作り直す
      if (
        event.type === Blockly.Events.BLOCK_DELETE &&
        event.oldJson?.type === "character_event" &&
        workspace.getBlocksByType("character_event", false).length === 0
      ) {
        createStartBlock(workspace, scale, offset);
        workspace.trashcan?.emptyContents();
      }

      notifyRemaining();

      // maxInstances 利用時、Blockly がブロック増減のたびにフライアウトを再描画して
      // transform（positionAt_ で設定した位置）をリセットするため、再描画の後に
      // 配置し直す。再描画は次フレーム以降に走るため十分に遅延させる。
      // ドラッグ中の再配置はジェスチャを破棄してドロップ不能になるため行わない
      // （ドロップ後の MOVE イベントで改めて予約される）。
      if (Object.keys(maxInstances).length > 0) {
        setTimeout(() => {
          if (workspace.rendered && !workspace.isDragging()) {
            updateFlyout(workspace, offset * scale, scale);
          }
        }, 200);
      }

      if (!workspace.isDragging()) {
        const code = BlocklyJs.workspaceToCode(workspace).replace(/function /g, "async $&");
        setEvent(code);
      }
    };

    workspace.addChangeListener(onWorkspaceChange);
    return () => workspace.removeChangeListener(onWorkspaceChange);
  }, [workspace, scale, offset]);

  useEffect(() => {
    return () => {
      if (workspace) workspace.dispose();
    };
  }, []);
};

export const useBlocklyInit = (
  scale: number,
  offset: number,
  locale: string,
  enabledCommands: BuilderCommandId[],
  maxInstances: { [type: string]: number },
  workspace: Blockly.WorkspaceSvg,
  setWorkspace: (workspace: Blockly.WorkspaceSvg) => void,
  setEvent: (code: string) => void,
  onRemainingChange: (remaining: { [type: string]: number }) => void,
) => {
  useInitPreviewWorkspace(scale, offset, locale, enabledCommands, maxInstances, setWorkspace);
  useWorkspaceListener(scale, offset, workspace, maxInstances, setEvent, onRemainingChange);
};
